import * as React from "react";
import { useSidebarState, useGetList } from "react-admin";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  SpaceDashboardOutlined as DashboardIcon,
  PeopleAltOutlined as PeopleIcon,
  EventOutlined as EventIcon,
  PaymentsOutlined as PaymentsIcon,
  ArticleOutlined as ArticleIcon,
  FavoriteBorderOutlined as CareIcon,
  CategoryOutlined as CategoryIcon,
  Diversity3Outlined as ReferralIcon,
  SupportAgentOutlined as SupportIcon,
  InsightsOutlined as InsightsIcon,
  ExpandMore,
} from "@mui/icons-material";
import { MENU_GROUPS, groupOf } from "./menuConfig";

const ICONS = {
  dashboard: DashboardIcon,
  people: PeopleIcon,
  event: EventIcon,
  payments: PaymentsIcon,
  article: ArticleIcon,
  care: CareIcon,
  category: CategoryIcon,
  referral: ReferralIcon,
  support: SupportIcon,
};

const STORAGE_KEY = "iouman_bo_menu_open";

const readOpen = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const writeOpen = (v) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch (_) {}
};

// Nombre de suggestions de spécialités en attente (GET /admin/suggestions/specialities
// ne renvoie que les PENDING).
export const usePendingSpecialities = () => {
  const { total, data } = useGetList(
    "suggestions-specialities",
    { pagination: { page: 1, perPage: 500 }, sort: { field: "createdAt", order: "DESC" }, filter: {} },
    { staleTime: 60_000, retry: false }
  );
  return total ?? data?.length ?? 0;
};

const CountBadge = ({ value }) =>
  value > 0 ? (
    <Box
      component="span"
      sx={{
        ml: 1,
        minWidth: 20,
        height: 20,
        px: 0.75,
        borderRadius: 10,
        bgcolor: "warning.main",
        color: "#fff",
        fontSize: 11,
        fontWeight: 700,
        display: "inline-grid",
        placeItems: "center",
      }}
      aria-label={`${value} en attente`}
    >
      {value > 99 ? "99+" : value}
    </Box>
  ) : null;

const itemSx = (selected) => (t) => ({
  borderRadius: 2.5,
  mx: 1,
  my: "1px",
  py: 0.6,
  pl: 5.25,
  pr: 1.25,
  minHeight: 34,
  color: selected ? "primary.main" : "text.secondary",
  bgcolor: selected ? alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.18 : 0.09) : "transparent",
  "&:hover": { bgcolor: selected ? alpha(t.palette.primary.main, 0.14) : t.palette.action.hover, color: "text.primary" },
});

const MenuEntry = ({ to, label, selected, badge }) => (
  <ListItemButton component={Link} to={to} selected={false} sx={itemSx(selected)} aria-current={selected ? "page" : undefined}>
    <ListItemText
      primary={
        <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>{label}</span>
          {badge}
        </Box>
      }
      primaryTypographyProps={{ variant: "body2", fontWeight: selected ? 600 : 500, fontSize: 13.5 }}
    />
  </ListItemButton>
);

export const AppMenu = () => {
  const [sidebarOpen, setSidebarOpen] = useSidebarState();
  const { pathname } = useLocation();
  const current = pathname.split("/")[1] || "";
  const pending = usePendingSpecialities();

  const [open, setOpen] = React.useState(() => {
    const saved = readOpen();
    const base = saved || {};
    const g = groupOf(current);
    return g ? { ...base, [g]: true } : base;
  });

  React.useEffect(() => {
    const g = groupOf(current);
    if (g) setOpen((o) => (o[g] ? o : { ...o, [g]: true }));
  }, [current]);

  const toggle = (key) =>
    setOpen((o) => {
      const next = { ...o, [key]: !o[key] };
      writeOpen(next);
      return next;
    });

  const badges = { pendingSpecialities: <CountBadge value={pending} /> };
  const dashSelected = pathname === "/" || pathname === "";

  return (
    <Box
      component="nav"
      aria-label="Navigation principale"
      sx={{ py: 1.5, pb: 6, width: sidebarOpen ? 264 : 64, transition: "width 0.2s", overflowX: "hidden" }}
    >
      <List disablePadding>
        <ListItemButton
          component={Link}
          to="/"
          sx={(t) => ({
            ...itemSx(dashSelected)(t),
            pl: 1.5,
            mb: 0,
          })}
        >
          <ListItemIcon sx={{ minWidth: 34, color: dashSelected ? "primary.main" : "text.secondary" }}>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          {sidebarOpen && (
            <ListItemText primary="Tableau de bord" primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
          )}
        </ListItemButton>

        {MENU_GROUPS.map((g) => {
          const Icon = ICONS[g.icon] || CategoryIcon;
          const items = g.items;
          // Groupe « Tableau de bord » : entrées à plat sous le lien d'accueil.
          if (g.key === "dashboard") {
            return items.map((i) => {
              const sel = current === i.resource;
              return (
                <Tooltip key={i.resource} title={sidebarOpen ? "" : i.label} placement="right">
                  <ListItemButton component={Link} to={`/${i.resource}`} sx={(t) => ({ ...itemSx(sel)(t), pl: 1.5, mb: 0.5 })}>
                    <ListItemIcon sx={{ minWidth: 34, color: sel ? "primary.main" : "text.secondary" }}>
                      <InsightsIcon fontSize="small" />
                    </ListItemIcon>
                    {sidebarOpen && (
                      <ListItemText primary={i.label} primaryTypographyProps={{ variant: "body2", fontWeight: 600 }} />
                    )}
                  </ListItemButton>
                </Tooltip>
              );
            });
          }
          const activeInGroup = items.some((i) => i.resource === current);
          const groupBadge = g.items.some((i) => i.badge === "pendingSpecialities") ? <CountBadge value={pending} /> : null;
          if (!sidebarOpen) {
            return (
              <Tooltip key={g.key} title={g.label} placement="right">
                <ListItemButton
                  onClick={() => {
                    setSidebarOpen(true);
                    setOpen((o) => ({ ...o, [g.key]: true }));
                  }}
                  sx={(t) => ({ ...itemSx(activeInGroup)(t), pl: 1.5 })}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: activeInGroup ? "primary.main" : "text.secondary" }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            );
          }
          return (
            <Box key={g.key} sx={{ mt: 0.25 }}>
              <ListItemButton
                onClick={() => toggle(g.key)}
                aria-expanded={!!open[g.key]}
                sx={{ mx: 1, borderRadius: 2.5, py: 0.6, pl: 1.5, pr: 1, minHeight: 36 }}
              >
                <ListItemIcon sx={{ minWidth: 34, color: activeInGroup ? "primary.main" : "text.secondary" }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box component="span" sx={{ display: "flex", alignItems: "center" }}>
                      <span>{g.label}</span>
                      {!open[g.key] && groupBadge}
                    </Box>
                  }
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: 600,
                    color: activeInGroup ? "text.primary" : "text.primary",
                  }}
                />
                <ExpandMore
                  fontSize="small"
                  sx={{
                    color: "text.disabled",
                    transition: "transform 0.2s",
                    transform: open[g.key] ? "rotate(180deg)" : "none",
                  }}
                />
              </ListItemButton>
              <Collapse in={!!open[g.key]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ position: "relative" }}>
                  {items.map((i) => (
                    <MenuEntry
                      key={i.resource}
                      to={`/${i.resource}`}
                      label={i.label}
                      selected={current === i.resource}
                      badge={i.badge ? badges[i.badge] : null}
                    />
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>
      {sidebarOpen && (
        <Typography variant="caption" sx={{ display: "block", px: 3, mt: 3, color: "text.disabled" }}>
          ioUman · back-office
        </Typography>
      )}
    </Box>
  );
};

export default AppMenu;
